import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Card, Row, Col } from 'react-bootstrap';
import { Formik, FieldArray } from 'formik';
import { toast } from 'react-toastify';
import {
  obterRateioTemplate,
  salvarRateioTemplate,
  sugerirCodigoRateioTemplate,
  recalcularRateioTemplatePorUsuarios,
  recalcularRateioTemplatePorM2,
  listarDepartamentosRateioAtivos,
} from '~/Services/Service';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import GridDepartamentos from './components/GridDepartamentos';
import FormDepartamento from './components/FormDepartamento';
import TotalFooter from './components/TotalFooter';
import { rateioTemplateSchema } from './validationSchema';
import { extrairMensagemErro } from '~/Utils/Helpers';

const INITIAL_VALUES = {
  codigo: null,
  descricao: '',
  obs: '',
  contas: [],
};

const ModalRateioTemplate = ({ show, onHide, codigo, onSalvar }) => {
  const ehEdicao = codigo != null;

  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);
  const [loadingDados, setLoadingDados] = useState(false);
  const [departamentosOpts, setDepartamentosOpts] = useState([]);

  const [confirmRecalc, setConfirmRecalc] = useState({ show: false, tipo: null });
  const [loadingRecalc, setLoadingRecalc] = useState(false);

  // Carrega lookup de departamentos ativos
  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      try {
        const data = await listarDepartamentosRateioAtivos();
        if (!ativo) return;
        const opts = (data || []).map((d) => ({
          value: d.codigo,
          label: d.descricao,
        }));
        setDepartamentosOpts(opts);
      } catch (err) {
        if (!ativo) return;
        // Não silenciar: usuário precisa saber por que o select está vazio.
        toast.error(extrairMensagemErro(err) || 'Falha ao carregar departamentos.');
      }
    };
    carregar();
    return () => { ativo = false; };
  }, []);

  // Carrega dados do template (edit) ou sugere código (create)
  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setLoadingDados(true);
      try {
        if (ehEdicao) {
          const data = await obterRateioTemplate(codigo);
          if (!ativo) return;
          setInitialValues({
            codigo: data.codigo,
            descricao: data.descricao || '',
            obs: data.obs || '',
            contas: (data.contas || []).map((c) => ({
              departamento: c.departamento,
              descricao_depto: c.descricao_depto || '',
              percentual: Number(c.percentual) || 0,
            })),
          });
        } else {
          const resp = await sugerirCodigoRateioTemplate();
          if (!ativo) return;
          setInitialValues({
            ...INITIAL_VALUES,
            codigo: resp.codigo_sugerido || null,
          });
        }
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setLoadingDados(false);
      }
    };
    carregar();
    return () => { ativo = false; };
  }, [codigo, ehEdicao]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await salvarRateioTemplate({
        codigo: values.codigo,
        descricao: values.descricao,
        obs: values.obs || null,
        contas: values.contas.map((c) => ({
          departamento: c.departamento,
          percentual: Number(c.percentual),
        })),
      });
      toast.success(ehEdicao ? 'Template atualizado com sucesso.' : 'Template criado com sucesso.');
      onSalvar();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecalcular = (tipo) => {
    setConfirmRecalc({ show: true, tipo });
  };

  const handleConfirmarRecalc = async (confirmado, formik) => {
    const tipo = confirmRecalc.tipo;
    setConfirmRecalc({ show: false, tipo: null });
    if (!confirmado) return;

    setLoadingRecalc(true);
    try {
      let resp;
      if (tipo === 'usuarios') {
        resp = await recalcularRateioTemplatePorUsuarios(codigo);
      } else {
        resp = await recalcularRateioTemplatePorM2(codigo);
      }
      const contasAtualizadas = (resp.contas || []).map((c) => ({
        departamento: c.departamento,
        descricao_depto: c.descricao_depto || '',
        percentual: Number(c.percentual) || 0,
      }));
      formik.setFieldValue('contas', contasAtualizadas);
      toast.success(resp.mensagem || 'Recálculo realizado com sucesso.');
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingRecalc(false);
    }
  };

  const titulo = ehEdicao ? 'Editar Template de Rateio' : 'Novo Template de Rateio';

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
            {titulo}
          </Modal.Title>
        </Modal.Header>

        {loadingDados ? (
          <Modal.Body className="text-center py-5 text-muted">Carregando...</Modal.Body>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={rateioTemplateSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
          >
            {(formik) => {
              const somaAtual = (formik.values.contas || []).reduce(
                (s, c) => s + (Number(c.percentual) || 0),
                0
              );

              return (
                <>
                <Form onSubmit={formik.handleSubmit}>
                  <Modal.Body>
                    {/* Card 1 — Dados do Template */}
                    <Card className="mb-3">
                      <Card.Header>
                        <strong>Dados do Template</strong>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col xs={12} md={3}>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1 font-weight-bold">
                                Código
                              </Form.Label>
                              <Form.Control
                                type="number"
                                size="sm"
                                value={formik.values.codigo || ''}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f5f5f5' }}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={9}>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1 font-weight-bold">
                                Descrição <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                name="descricao"
                                maxLength={100}
                                value={formik.values.descricao}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={!!formik.errors.descricao}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.descricao}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-0">
                          <Form.Label className="small mb-1 font-weight-bold">
                            Observação
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            size="sm"
                            name="obs"
                            rows={2}
                            maxLength={4000}
                            value={formik.values.obs || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={!!formik.errors.obs}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.obs}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Card.Body>
                    </Card>

                    {/* Card 2 — Departamentos */}
                    <Card>
                      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                        <strong>Departamentos</strong>
                        {ehEdicao && (
                          <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={formik.values.contas.length === 0 || loadingRecalc || formik.isSubmitting}
                              onClick={() => handleRecalcular('usuarios')}
                              title="Recalcula percentuais proporcionalmente ao número de usuários por departamento"
                            >
                              Recalcular por Usuários
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={formik.values.contas.length === 0 || loadingRecalc || formik.isSubmitting}
                              onClick={() => handleRecalcular('m2')}
                              title="Recalcula percentuais proporcionalmente à área (m²) por departamento"
                            >
                              Recalcular por m²
                            </Button>
                          </div>
                        )}
                      </Card.Header>

                      <FieldArray name="contas">
                        {({ push, remove }) => (
                          <>
                            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                              <GridDepartamentos
                                contas={formik.values.contas}
                                codigoRateio={formik.values.codigo}
                                onRemover={remove}
                                disabled={formik.isSubmitting || loadingRecalc}
                              />
                            </div>
                            <TotalFooter contas={formik.values.contas} />

                            {typeof formik.errors.contas === 'string' && (
                              <div className="px-3 pb-1">
                                <small className="text-danger">{formik.errors.contas}</small>
                              </div>
                            )}

                            <div className="p-2">
                              <FormDepartamento
                                departamentosOpts={departamentosOpts}
                                contasAtuais={formik.values.contas}
                                somaAtual={somaAtual}
                                disabled={formik.isSubmitting || loadingRecalc}
                                onAdicionar={(novaConta) => {
                                  push(novaConta);
                                }}
                              />
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </Card>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={formik.isSubmitting}>
                      Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={formik.isSubmitting || loadingRecalc}>
                      {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Modal.Footer>
                </Form>

                {/* ModalConfirmacao para Recalcular — fora do Form mas dentro do Formik */}
                <ModalConfirmacao
                  show={confirmRecalc.show}
                  title={`Recalcular por ${confirmRecalc.tipo === 'usuarios' ? 'Usuários' : 'm²'}`}
                  message="Isso substitui os percentuais atuais dos departamentos do template. Prosseguir?"
                  onClose={(confirmado) => handleConfirmarRecalc(confirmado, formik)}
                />
                </>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
};

export default ModalRateioTemplate;
